import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Plus,
  Filter,
  Building2,
  UserCircle,
  BarChart3,
  Stethoscope
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import UsersSection from './UsersSection';
import UsageStats from './UsageStats';
import SystemDiagnostics from './SystemDiagnostics';
import CreateClientModal from './CreateClientModal';
import ClientsPanel, { PanelClient } from './ClientsPanel';
import ClientAdminPage from './ClientAdminPage';
import { useAuth } from '../../contexts/AuthContext';
import { saveAdminClientOrder, loadAdminClientOrder } from '../../services/userPreferences';
import { getStoredSelectedClientRaw } from '../../utils/clientStorage';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import api from '../../utils/api';

interface TomaInfo {
  tableName: string;
  displayName: string;
  fecha?: string;
}

const normalizeType = (t?: string): 'demo' | 'client' =>
  t === 'demo' || t === 'demo_customer' ? 'demo' : 'client';

const STALE_HOURS = 6;
const SYNC_CONCURRENCY = 5;
const isStale = (iso?: string | null): boolean => {
  if (!iso) return true;
  const ageMs = Date.now() - new Date(iso).getTime();
  return ageMs > STALE_HOURS * 3600 * 1000;
};

export interface SyncProgress {
  total: number;
  completed: number;
  failed: number;
  active: boolean;
}
const EMPTY_PROGRESS: SyncProgress = { total: 0, completed: 0, failed: 0, active: false };

let __autoSyncFired = false;

// Lee el param ?editClient una sola vez al primer mount real. Sobrevive a
// StrictMode remounts (useState init corre 2x) — siempre devuelve el mismo
// valor capturado en la primera lectura del URL.
let __editClientUidCaptured: string | null | undefined = undefined;
function readEditClientFromUrl(): string | null {
  if (__editClientUidCaptured !== undefined) return __editClientUidCaptured;
  if (typeof window === 'undefined') { __editClientUidCaptured = null; return null; }
  const uid = new URLSearchParams(window.location.search).get('editClient');
  __editClientUidCaptured = uid;
  return uid;
}

// Invalida el cache module-level de editClient para que el PRÓXIMO mount de
// ClientsManagement vuelva a leer ?editClient= del URL. Debe llamarse ANTES
// de disparar el cambio de estado que monta ClientsManagement por primera vez
// en la sesión (botón CONFIG de la barra superior desde la vista de datos).
// Sin esto, si ClientsManagement ya se montó antes en la sesión, el cache
// devuelve su valor stale (null) y aterriza en la lista System Admin en vez
// de ClientAdminPage (PT-#40 / F-E3-1).
export function resetEditClientCache(): void {
  __editClientUidCaptured = undefined;
}


type Client = PanelClient;

const ClientsManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'clients' | 'users' | 'stats' | 'diagnostico'>('clients');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState<string | null>(null);
  const [uploadingGreetingVideo, setUploadingGreetingVideo] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [syncingTomas, setSyncingTomas] = useState(false);
  const [syncingDemos, setSyncingDemos] = useState(false);
  const [clientsProgress, setClientsProgress] = useState<SyncProgress>(EMPTY_PROGRESS);
  const [demosProgress, setDemosProgress] = useState<SyncProgress>(EMPTY_PROGRESS);
  const { userData, currentUser } = useAuth();
  // Orden custom del admin (persistido por user en Firestore)
  const [customOrder, setCustomOrder] = useState<{ real: string[]; demo: string[] }>({ real: [], demo: [] });
  useEffect(() => {
    if (!currentUser?.uid) return;
    loadAdminClientOrder(currentUser.uid).then(o => {
      if (o) setCustomOrder({ real: o.real || [], demo: o.demo || [] });
    });
  }, [currentUser?.uid]);

  const applyCustomOrder = (list: Client[], orderUids: string[]): Client[] => {
    if (!orderUids || orderUids.length === 0) return list;
    const byUid = new Map(list.map(c => [c.uid, c]));
    const ordered: Client[] = [];
    // Primero los que están en el orden custom (en ese orden)
    for (const uid of orderUids) {
      const c = byUid.get(uid);
      if (c) { ordered.push(c); byUid.delete(uid); }
    }
    // Después los que NO están en el orden custom (clientes nuevos), al final
    byUid.forEach(c => ordered.push(c));
    return ordered;
  };

  const handleReorderReal = (uids: string[]) => {
    setCustomOrder(prev => ({ ...prev, real: uids }));
    if (currentUser?.uid) saveAdminClientOrder(currentUser.uid, { real: uids });
  };
  const handleReorderDemo = (uids: string[]) => {
    setCustomOrder(prev => ({ ...prev, demo: uids }));
    if (currentUser?.uid) saveAdminClientOrder(currentUser.uid, { demo: uids });
  };
  const { t } = useTranslation();
  const isSuperAdmin = userData?.user_type === 'superadmin';

  // ── Diagnóstico del sistema (portado del AdminPanel deprecado) ──
  // SystemDiagnostics necesita la lista de tomas + una toma seleccionada.
  const [tables, setTables] = useState<TomaInfo[]>([]);
  const [diagnosticTable, setDiagnosticTable] = useState<string>('');

  const formatDateOnly = (date: Date): string => {
    const monthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    const day = date.getDate();
    const month = t(`months.${monthKeys[date.getMonth()]}`);
    const year = date.getFullYear();
    const preposition = t('date.date_preposition');
    return `${day} ${preposition} ${month}, ${year}`;
  };

  const getTableDropdownName = (tableName: string, fecha?: string): string => {
    if (fecha && typeof fecha === 'string') {
      const dateMatch = fecha.match(/^(\d{4})-(\d{2})-(\d{2})/);
      let date: Date;
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        date.setHours(0, 0, 0, 0);
      } else {
        date = new Date(fecha);
      }
      if (!isNaN(date.getTime())) return formatDateOnly(date);
    }
    const numberMatch = tableName.match(/(\d+)$/);
    if (numberMatch) {
      const tomaNumber = parseInt(numberMatch[1]);
      const baseDate = new Date(2025, 4, 4);
      if (tomaNumber >= 1) baseDate.setDate(baseDate.getDate() + (tomaNumber - 1));
      return formatDateOnly(baseDate);
    }
    return tableName;
  };

  // Cargar tablas disponibles para el diagnóstico (solo cuando se abre la tab).
  useEffect(() => {
    if (activeTab !== 'diagnostico' || tables.length > 0) return;
    const loadTables = async () => {
      try {
        // A nivel System Admin no hay cliente seleccionado (admin-mode): el backend
        // no puede resolver la DB del cliente y devuelve 500. En ese caso no
        // disparamos el request y dejamos el empty-state ("Seleccionar toma...").
        const headers: any = {};
        const selectedClientStr = getStoredSelectedClientRaw();
        if (!selectedClientStr) return;
        let clientId: string | undefined;
        let clientName: string | undefined;
        try {
          const client = JSON.parse(selectedClientStr);
          clientId = client.clientId;
          clientName = client.clientName;
        } catch (e) {
          console.error('Error parsing client:', e);
          return;
        }
        if (!clientId || clientId === 'admin-mode') return;
        headers['X-Client-Id'] = clientId;
        headers['X-Client-Name'] = clientName;
        const response = await axios.get('/api/tomas-con-fechas', { headers });
        const tablesData: TomaInfo[] = response.data.map((table: any) => ({
          tableName: table.tableName,
          displayName: getTableDropdownName(table.tableName, table.fecha),
          fecha: table.fecha,
        }));
        setTables(tablesData);
        if (tablesData.length > 0 && !diagnosticTable) {
          setDiagnosticTable(tablesData[0].tableName);
        }
      } catch (error) {
        console.error('Error loading tables:', error);
      }
    };
    loadTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Soporte deep-link: /?editClient=<uid> abre directo la pantalla Administrar Cliente.
  // Lectura ROBUSTA contra StrictMode: useState init corre 2x en dev, así que
  // capturamos el valor en una variable module-level una sola vez. La limpieza
  // del query param se hace después en useEffect (no en init).
  const [autoOpenEditUid, setAutoOpenEditUid] = useState<string | null>(() => readEditClientFromUrl());
  useEffect(() => {
    // Una vez que ClientsManagement se montó y leyó el param, lo borramos del URL.
    if (autoOpenEditUid && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('editClient')) {
        params.delete('editClient');
        const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listener de popstate: cuando otro componente (ClientCard "Config") cambia
  // el URL con pushState + dispatch popstate, releemos editClient.
  useEffect(() => {
    const handler = () => {
      const uid = new URLSearchParams(window.location.search).get('editClient');
      if (uid) {
        // Reset cache module-level y aplicar nuevo valor
        // @ts-ignore — reset al valor recibido
        __editClientUidCaptured = uid;
        setAutoOpenEditUid(uid);
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);

      // Phase 1: Fetch client list from Firestore and show cards immediately
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      const clientsData: Client[] = [];

      clientsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        clientsData.push({
          uid: docSnap.id,
          name: data.name,
          display_name: data.name,
          database: data.database,
          logo: data.logo,
          welcomeVideo: data.welcomeVideo,
          greetingVideo: data.greetingVideo,
          active: data.active,
          client_type: data.client_type,
          catalogadoPrompt: data.catalogadoPrompt,
          enable_tags: data.enable_tags,
          enable_sku: data.enable_sku,
          last_sync_at: data.last_sync_at,
          // Camino A: leer denormalizado del doc raíz (sin Phase 2)
          tableCount: typeof data.tableCount === 'number' ? data.tableCount : 0,
          lastTomaDate: data.lastTomaDate || null,
          tableError: data.last_sync_status === 'failed' ? true : false,
          tableErrorMessage: data.last_sync_error || undefined,
          userCount: 0,
        });
      });

      // Sort and show cards immediately (tomas count shows spinner)
      const sortedClients = clientsData.sort((a, b) => {
        const nameA = (a.display_name || a.name).toLowerCase();
        const nameB = (b.display_name || b.name).toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setClients(sortedClients);
      setLoading(false);
      // Camino A: ya no necesitamos Phase 2 individual. Todo viene denormalizado
      // del doc raíz `clients/{id}`. El stale-check + sync masivo se encarga de
      // mantenerlo fresco cuando >6h.
    } catch (error) {
      console.error('Error fetching clients:', error);
      setLoading(false);
    }
  };

  const handleVideoUpload = async (file: File, clientId: string, videoType: 'welcomeVideo' | 'greetingVideo') => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      alert('Por favor sube un archivo de video válido (MP4, WebM, OGG)');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('El video no puede superar los 50MB');
      return;
    }

    if (videoType === 'welcomeVideo') {
      setUploadingVideo(clientId);
    } else {
      setUploadingGreetingVideo(clientId);
    }

    try {
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `greeting-videos/${timestamp}-${file.name}`;
      
      const storageRef = ref(storage, fileName);

      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update client in Firestore
      const clientRef = doc(db, 'clients', clientId);
      await updateDoc(clientRef, {
        [videoType]: downloadURL
      });

      // Update local state
      setClients(prevClients => 
        prevClients.map(client => 
          client.uid === clientId 
            ? { ...client, [videoType]: downloadURL }
            : client
        )
      );

      alert(`Video ${videoType === 'welcomeVideo' ? 'de bienvenida' : 'de saludo'} subido exitosamente`);
    } catch (error: any) {
      console.error('Error uploading video:', error);
      if (error.code === 'storage/unauthorized') {
        alert('Error: No autorizado para subir archivos. Por favor verifica las reglas de Firebase Storage.');
      } else if (error.message) {
        alert(`Error: ${error.message}`);
      } else {
        alert('Error al subir el video');
      }
    } finally {
      setUploadingVideo(null);
      setUploadingGreetingVideo(null);
    }
  };

  // Sync paralelo por cliente con progress visible.
  const syncClientsList = async (
    list: Client[],
    setProgress: React.Dispatch<React.SetStateAction<SyncProgress>>,
    setSyncingFlag: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (list.length === 0) return;
    setSyncingFlag(true);
    setProgress({ total: list.length, completed: 0, failed: 0, active: true });

    let idx = 0;
    const worker = async () => {
      while (idx < list.length) {
        const i = idx++;
        const client = list[i];
        let success = false;
        try {
          // Forzar x-client-id al cliente correcto (override del 'admin-mode' del interceptor)
          const res = await api.post(`/api/clients/${client.uid}/sync-tomas`, {}, {
            headers: { 'x-client-id': client.uid }
          });
          if (res.data?.success === false) {
            console.warn(`sync returned failure for ${client.uid}:`, res.data?.error);
          } else {
            // Patch optimista del timestamp + re-fetch del count para evitar
            // race con Phase 2 (cards que leyeron antes del sync ven 0 tomas).
            applyClientPatch(client.uid, { last_sync_at: new Date().toISOString() });
            try {
              const tres = await api.get(`/api/clients/${client.uid}/tomas`, { timeout: 5000 });
              if (tres.data?.success) {
                applyClientPatch(client.uid, {
                  tableCount: tres.data.tomas?.length || 0,
                  lastTomaDate: tres.data.lastTomaDate || null,
                });
              }
            } catch { /* no-fatal */ }
            success = true;
          }
        } catch (err) {
          console.warn(`sync threw for ${client.uid}:`, err);
        }
        setProgress(prev => ({
          ...prev,
          completed: prev.completed + (success ? 1 : 0),
          failed: prev.failed + (success ? 0 : 1),
        }));
      }
    };

    await Promise.all(Array.from({ length: SYNC_CONCURRENCY }, () => worker()));
    setProgress(prev => ({ ...prev, active: false }));
    setSyncingFlag(false);
  };

  const handleSyncTomas = () => syncClientsList(
    realClients.filter(c => c.active !== false),
    setClientsProgress,
    setSyncingTomas
  );
  const handleSyncDemos = () => syncClientsList(
    demoClients.filter(c => c.active !== false),
    setDemosProgress,
    setSyncingDemos
  );

  // Patch optimista local (evita re-fetch al cambiar tipo/estado/etc desde modal)
  const applyClientPatch = (uid: string, patch: Record<string, any>) => {
    setClients(prev => prev.map(c => c.uid === uid ? { ...c, ...patch } : c));
  };

  const filteredClients = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return clients.filter(client =>
      (client.display_name?.toLowerCase() || '').includes(term) ||
      client.name.toLowerCase().includes(term)
    );
  }, [clients, searchTerm]);

  const { realClients, demoClients } = useMemo(() => {
    const real: Client[] = [];
    const demo: Client[] = [];
    for (const c of filteredClients) {
      if (normalizeType(c.client_type) === 'demo') demo.push(c);
      else real.push(c);
    }
    return {
      realClients: applyCustomOrder(real, customOrder.real),
      demoClients: applyCustomOrder(demo, customOrder.demo),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredClients, customOrder.real, customOrder.demo]);

  // last_sync_at si existe (con hora), sino fallback a max(lastTomaDate) que es solo fecha.
  // Devolvemos { ts, hasTime } para que el formatter decida si mostrar hora.
  const maxLastSync = (list: Client[]): { ts: string | null; hasTime: boolean } => {
    let maxSync: string | null = null;
    let maxToma: string | null = null;
    for (const c of list) {
      if (c.active === false) continue;
      if (c.last_sync_at && (!maxSync || c.last_sync_at > maxSync)) maxSync = c.last_sync_at;
      if (c.lastTomaDate && (!maxToma || c.lastTomaDate > maxToma)) maxToma = c.lastTomaDate;
    }
    if (maxSync) return { ts: maxSync, hasTime: true };
    if (maxToma) return { ts: maxToma, hasTime: false };
    return { ts: null, hasTime: false };
  };
  const clientsLastSync = useMemo(() => maxLastSync(realClients), [realClients]);
  const demosLastSync = useMemo(() => maxLastSync(demoClients), [demoClients]);

  // Stale-while-revalidate: al cargar, sincronizar en background los activos
  // cuyo last_sync_at sea > 6h. Renderizamos inmediato con datos actuales,
  // el panel muestra el progreso. Solo admin/superadmin.
  // Dedupe entre StrictMode remounts via flag module-level.
  useEffect(() => {
    if (!isSuperAdmin || loading || __autoSyncFired) return;
    if (clients.length === 0) return;

    const staleClients = realClients.filter(c => c.active !== false && isStale(c.last_sync_at));
    // 2026-06-02: demos NO se auto-sincronizan (la mayoría tiene servers caídos
    // y bloqueaban el load del admin con 10s/cliente de timeout MariaDB).
    // Sync demos = solo manual via botón "Sincronizar demos".
    console.log('[stale-check]',
      'realClients:', realClients.length,
      'staleClients:', staleClients.length,
      'demoClients:', demoClients.length,
      'sample last_sync_at:', realClients.slice(0, 3).map(c => ({ uid: c.uid, last: c.last_sync_at })));
    if (staleClients.length === 0) return;

    __autoSyncFired = true;
    if (staleClients.length > 0) syncClientsList(staleClients, setClientsProgress, setSyncingTomas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, loading, clients.length]);

  // Si hay deep-link a editClient, renderizar la pantalla "Administrar Cliente"
  // en lugar del listado.
  const adminTargetClient = autoOpenEditUid
    ? clients.find(c => c.uid === autoOpenEditUid)
    : null;
  if (adminTargetClient) {
    return (
      <ClientAdminPage
        client={adminTargetClient as any}
        onBack={() => setAutoOpenEditUid(null)}
        onClientPatch={applyClientPatch}
      />
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-6">
        {/* Header — tipografía 20% más grande, padding top reducido */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">
            System Admin
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Clientes, usuarios, estadísticas de uso y diagnóstico del sistema
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('clients')}
                className={`
                  py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2
                  ${activeTab === 'clients'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Building2 className="w-5 h-5" />
                Clientes
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`
                  py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2
                  ${activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <UserCircle className="w-5 h-5" />
                Usuarios
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`
                  py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2
                  ${activeTab === 'stats'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <BarChart3 className="w-5 h-5" />
                Stats
              </button>
              <button
                onClick={() => setActiveTab('diagnostico')}
                className={`
                  py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2
                  ${activeTab === 'diagnostico'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Stethoscope className="w-5 h-5" />
                Diagnóstico
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'clients' && (
          <>
            {/* Search and Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar clientes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setFilterOpen(!filterOpen)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <Filter className="w-4 h-4" />
                  Filtros
                </button>
                <button
                  onClick={() => setCreateModalOpen(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t('admin.addClient', 'Nuevo Cliente')}
                </button>
              </div>
            </div>

            <ClientsPanel
              title={t('admin.panel.clientsTitle', 'Clientes')}
              clients={realClients}
              loading={loading}
              onRefresh={fetchClients}
              onVideoUpload={handleVideoUpload}
              uploadingVideo={uploadingVideo}
              uploadingGreetingVideo={uploadingGreetingVideo}
              onClientPatch={applyClientPatch}
              showSyncButton={isSuperAdmin}
              onSync={handleSyncTomas}
              syncing={syncingTomas}
              syncLabel={t('admin.panel.syncClients', 'Sincronizar tomas')}
              lastSyncAt={clientsLastSync.ts}
              lastSyncHasTime={clientsLastSync.hasTime}
              syncProgress={clientsProgress}
              autoOpenEditUid={autoOpenEditUid}
              onReorder={handleReorderReal}
            />

            <ClientsPanel
              title={t('admin.panel.demosTitle', 'Demos')}
              clients={demoClients}
              loading={loading}
              onRefresh={fetchClients}
              onVideoUpload={handleVideoUpload}
              uploadingVideo={uploadingVideo}
              uploadingGreetingVideo={uploadingGreetingVideo}
              onClientPatch={applyClientPatch}
              showSyncButton={isSuperAdmin}
              onSync={handleSyncDemos}
              syncing={syncingDemos}
              syncLabel={t('admin.panel.syncDemos', 'Sincronizar demos')}
              lastSyncAt={demosLastSync.ts}
              lastSyncHasTime={demosLastSync.hasTime}
              syncProgress={demosProgress}
              autoOpenEditUid={autoOpenEditUid}
              onReorder={handleReorderDemo}
            />

            {filteredClients.length === 0 && !loading && (
              <div className="text-center py-12">
                <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t('admin.panel.noClientsFound', 'No se encontraron clientes')}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm
                    ? t('admin.panel.tryOtherTerms', 'Intentá con otros términos de búsqueda')
                    : t('admin.panel.startAdding', 'Comenzá agregando un nuevo cliente')}
                </p>
              </div>
            )}
          </>
        )}
        {activeTab === 'users' && <UsersSection />}
        {activeTab === 'stats' && <UsageStats />}
        {activeTab === 'diagnostico' && (
          <SystemDiagnostics
            selectedTable={diagnosticTable}
            tables={tables}
            onTableChange={setDiagnosticTable}
          />
        )}
      </div>

      {/* Create Client Modal */}
      <CreateClientModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={() => {
          setCreateModalOpen(false);
          fetchClients();
        }}
      />
    </div>
  );
};

export default ClientsManagement;
